import { inject, Injectable } from '@angular/core';
import { GeoLoactionRepository } from '../../domain/geolocation.repository';
import {
  GeoCountryResponse,
  GeoCountryWithStateResponse,
  GeoCountryDetailResponse,
  GeoStateResponse,
  GeoCityResponse,
  GeoBank,
  GeoBankByAccount,
  GeoPostalLocation,
  GeoBankResponse,
} from '../../domain/geolocation.domain';
import { firstValueFrom } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiUrlsService } from '@app/shared/constants/urls.contants';

@Injectable()
export class GeolocationHttpRepository extends GeoLoactionRepository {
  private readonly _http = inject(HttpClient);
  private readonly urls = inject(ApiUrlsService);

  getCountries(): Promise<GeoCountryResponse[]> {
    return firstValueFrom(this._http.get<GeoCountryResponse[]>(this.urls.GET_COUNTRIES_URL));
  }

  getCountriesWithStates(): Promise<GeoCountryWithStateResponse[]> {
    return firstValueFrom(this._http.get<GeoCountryWithStateResponse[]>(this.urls.GET_COUNTRIES_WITH_STATES_URL));
  }

  getCountryDetails(countryCode: string): Promise<GeoCountryDetailResponse> {
    return firstValueFrom(this._http.get<GeoCountryDetailResponse>(this.urls.GET_COUNTRY_DETAILS_URL(countryCode.toLowerCase())));
  }

  getStatesByCountry(countryCode: string): Promise<GeoStateResponse[]> {
    return firstValueFrom(this._http.get<GeoStateResponse[]>(this.urls.GET_STATES_BY_COUNTRY_URL(countryCode)));
  }

  getCitiesByState(stateCode: string): Promise<GeoCityResponse[]> {
    return firstValueFrom(this._http.get<GeoCityResponse[]>(this.urls.GET_CITIES_BY_STATE_URL(stateCode.toLocaleLowerCase())));
  }

  async getBanksByCountry(countryCode: string, flowCode?: string): Promise<GeoBank[]> {
    const params = flowCode ? new HttpParams().set('flowCode', flowCode) : undefined;
    const response = await firstValueFrom(
      this._http.get<{ banks: { institutionCode: string; institutionName: string }[] }>(
        this.urls.GET_BANKS_BY_COUNTRY_URL(countryCode.toLowerCase()),
        params ? { params } : {}
      )
    );
    return (response?.banks ?? []).map(b => ({
      bankCode: b.institutionCode,
      bankName: b.institutionName,
    }));
  }

  getBanksByCountryAndRoutingNumber(countryCode: string, routingNumber: string): Promise<GeoBankResponse> {
    return firstValueFrom(
      this._http.get<GeoBankResponse>(
        `${this.urls.GET_BANKS_BY_COUNTRY_URL(countryCode.toLowerCase())}/${routingNumber}`
      )
    );
  }

  async getBankByAccount(account: string, countryCode: string): Promise<GeoBankByAccount | null> {
    const params = new HttpParams()
      .set('account', account)
      .set('countryCode', countryCode);
    try {
      return await firstValueFrom(
        this._http.get<GeoBankByAccount>(this.urls.GET_BANK_BY_ACCOUNT_URL, { params })
      );
    } catch {
      return null;
    }
  }

  async getPostalLocationsByCountry(countryCode: string, postalCode: string): Promise<GeoPostalLocation[]> {
    const params = new HttpParams().set('postalCode', postalCode);
    const response = await firstValueFrom(
      this._http.get<{ postalLocations: { regionName: string; localityName: string }[] }>(
        this.urls.GET_POSTAL_LOCATIONS_URL(countryCode.toLowerCase()), { params }
      )
    );
    return (response?.postalLocations ?? []).map(p => ({
      postalCode,
      cityName: p.localityName,
      stateName: p.regionName,
    }));
  }

  async getRegionsByCountry(countryCode: string): Promise<GeoStateResponse[]> {
    const locations = await firstValueFrom(
      this._http.get<{ postalLocations: { regionName: string; localityName: string | null; postalCode: string | null }[] }>(
        this.urls.GET_POSTAL_LOCATIONS_URL(countryCode.toLowerCase())
      )
    );
    const seen = new Set<string>();
    const regions: GeoStateResponse[] = [];
    for (const item of locations?.postalLocations ?? []) {
      if (item.regionName && !seen.has(item.regionName)) {
        seen.add(item.regionName);
        regions.push({ id: 0, stateCode: item.regionName, stateName: item.regionName });
      }
    }
    return regions;
  }

  async getComunasByRegion(countryCode: string, regionName: string): Promise<GeoCityResponse[]> {
    const params = new HttpParams().set('regionName', regionName);
    const locations = await firstValueFrom(
      this._http.get<{ postalLocations: { regionName: string; localityName: string | null; postalCode: string | null }[] }>(
        this.urls.GET_POSTAL_LOCATIONS_URL(countryCode.toLowerCase()), { params }
      )
    );
    const seen = new Set<string>();
    const cities: GeoCityResponse[] = [];
    for (const item of locations?.postalLocations ?? []) {
      if (item.localityName && !seen.has(item.localityName)) {
        seen.add(item.localityName);
        cities.push({ id: 0, cityCode: item.localityName, cityName: item.localityName });
      }
    }
    return cities;
  }

  async getPostalCodeByLocation(countryCode: string, regionName: string, localityName: string): Promise<string | null> {
    const params = new HttpParams()
      .set('regionName', regionName)
      .set('localityName', localityName);
    const response = await firstValueFrom(
      this._http.get<{ postalLocations: { postalCode: string | null }[] }>(
        this.urls.GET_POSTAL_LOCATIONS_URL(countryCode.toLowerCase()), { params }
      )
    );
    return response?.postalLocations?.[0]?.postalCode ?? null;
  }
}
